import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Subscription } from 'rxjs';
import { filter, distinctUntilChanged, map, mergeMap } from 'rxjs/operators';
import { AccountService } from './account/account.service';
import { UtilService } from './shared/services/util.service';
import { AuthStatusService } from './shared/services/auth-status.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {
  title = 'Certiplate';
  showHeader = false;
  showFooter = true;
  showSidebar = true;
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  sidebarExpanded = false;
  isRefreshingToken$ = this.authStatusService.isRefreshingToken$;
  private readonly subscriptions = new Subscription();

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private accSrvc: AccountService,
    private util: UtilService,
    private authStatusService: AuthStatusService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    this.subscriptions.add(
      this.router.events
        .pipe(
          filter((event) => event instanceof NavigationEnd),
          map(() => this.activatedRoute),
          map((route) => {
            while (route.firstChild) {
              route = route.firstChild;
            }
            return route;
          }),
          mergeMap((route) => route.data),
          distinctUntilChanged()
        )
        .subscribe((data) => {
          // Use route data to determine visibility
          this.showHeader = data?.showHeader ?? true;
          this.showFooter = data?.showFooter ?? true;
          this.showSidebar = data?.showSidebar ?? true;

          // Force change detection
          this.changeDetectorRef.detectChanges();
        })
    );

    // Idle configuration
    idle.setIdle(5);
    idle.setTimeout(5);
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    idle.onIdleEnd.subscribe(() => (this.idleState = 'No longer idle.'));
    idle.onTimeout.subscribe(() => {
      this.idleState = 'Timed out!';
      this.timedOut = true;
    });
    idle.onIdleStart.subscribe(() => (this.idleState = "You've gone idle!"));
    idle.onTimeoutWarning.subscribe(
      (countdown) =>
        (this.idleState = 'You will time out in ' + countdown + ' seconds!')
    );

    // Keepalive configuration
    keepalive.interval(15);
    keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    this.reset();
  }

  async ngOnInit() {
    if (this.accSrvc.jwt && this.accSrvc.refreshToken) {
      this.authStatusService.setRefreshingTokenStatus(true);
      try {
        await this.util.refreshAccessToken(this.accSrvc.refreshToken);
        this.changeDetectorRef.markForCheck();
      } catch (err) {
        console.error('Error during token refresh:', err);
      } finally {
        this.authStatusService.setRefreshingTokenStatus(false);
        this.changeDetectorRef.markForCheck();
      }
    } else {
      this.authStatusService.setRefreshingTokenStatus(false);
      this.changeDetectorRef.markForCheck();
    }
  }  

  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }

  onSidebarToggle(expanded: boolean) {
    this.sidebarExpanded = expanded;
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 300);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}